import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { gallery } from '../data/site';
import { useLanguage } from '../contexts/LanguageContext';
import { useMotionPreferences } from '../hooks/useMotionPreferences';
import { fadeScale, fadeUp, staggerContainer, viewportOnce } from '../utils/motion';
const spanClasses = {
  row: 'sm:row-span-2',
  wide: 'sm:col-span-2',
  default: ''
};
export function Community() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    reduced,
    parallax
  } = useMotionPreferences();
  const {
    t
  } = useLanguage();
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const mosaicY = useTransform(scrollYProgress, [0, 1], [40 * parallax, -40 * parallax]);
  const item = fadeUp(reduced, 28);
  const photo = fadeScale(reduced);
  return <section id="community" ref={sectionRef} className="relative w-full overflow-hidden py-28" data-id="element-13" data-mp="1abkoapu5l58f">
      <div className="pointer-events-none absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-grape/20 blur-[150px]" data-id="element-14" data-mp="1fa5qgusytyzi" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8" data-id="element-15" data-mp="d40zfj9zmu2o">
        <div className="grid gap-14 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:items-center" data-id="element-16" data-mp="d4kg6wnwjcf4">
          <motion.div variants={staggerContainer(0.1)} initial="hidden" whileInView="show" viewport={viewportOnce} data-id="element-17" data-mp="94aevxb21a6l">
            <motion.p variants={item} className="text-xs font-semibold uppercase tracking-[0.28em] text-magenta" data-id="element-18" data-mp="3va5ueelmpx4v">
              {t.community.eyebrow}
            </motion.p>
            <motion.h2 variants={item} className="neon-text mt-4 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-5xl" data-id="element-19" data-mp="3ljuf5vwhw1io">
              {t.community.titleLines[0]}
              <br data-id="element-20" data-mp="37j9xx468adra" />
              <span className="bg-gradient-to-r from-electric via-magenta to-grape bg-clip-text text-transparent" data-id="element-21" data-mp="37kurorkpxso3">
                {t.community.titleLines[1]}
              </span>
            </motion.h2>
            <motion.p variants={item} className="mt-6 text-base leading-relaxed text-white/60" data-id="element-22" data-mp="32wbsj2wmkdkg">
              {t.community.body}
            </motion.p>

            <motion.dl variants={item} className="mt-10 grid grid-cols-2 gap-6 border-t border-white/10 pt-8" data-id="element-23" data-mp="2sx4iaol6sz1k">
              {t.community.stats.map(stat => <div key={stat.label} data-id="element-24" data-mp="3lls72u55a6jv">
                  <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-white/45" data-id="element-25" data-mp="3lkqlhvqi61nw">
                    {stat.label}
                  </dt>
                  <dd className="mt-2 font-display text-3xl font-black text-white" data-id="element-26" data-mp="3bvyym20au7ju">{stat.value}</dd>
                </div>)}
            </motion.dl>
          </motion.div>

          <motion.div variants={staggerContainer(0.09)} initial="hidden" whileInView="show" viewport={viewportOnce} style={{
          y: mosaicY,
          willChange: 'transform'
        }} className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-27" data-mp="3e2icbf0jokv">
            {gallery.map(image => <motion.figure key={image.src} variants={photo} whileHover={reduced ? undefined : {
            scale: 1.02
          }} transition={{
            type: 'spring',
            stiffness: 240,
            damping: 22
          }} className={`group relative overflow-hidden rounded-2xl border border-white/10 ${spanClasses[image.span]}`} data-id="element-28" data-mp="22nlvepbg1m5i">
                <img src={image.src} alt={image.alt} loading="lazy" className="h-full min-h-[12rem] w-full object-cover transition-transform duration-700 will-change-transform group-hover:scale-105" data-id="element-29" data-mp="1j062ywafz5en" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#07051A]/70 to-transparent" data-id="element-30" data-mp="2qdg9193l7eqt" />
                <div className="pointer-events-none absolute inset-0 opacity-0 shadow-[inset_0_0_60px_-10px_rgba(236,30,156,0.9)] transition-opacity duration-500 group-hover:opacity-100" data-id="element-31" data-mp="3lo6a8y6p058u" />
              </motion.figure>)}
          </motion.div>
        </div>
      </div>
    </section>;
}